// Variables for default theme

// VARIABLES
// Colors
const brandColor = '#2d32aa'
const brandSecondaryColor = '#00b8de'
const error = '#f9423a'
const success = '#89ce23'
const warning = '#ff9d00'
const info = '#0089ff'
const white = '#fff'
const black = '#000'

// Font Family
const fontFamily = 'NexiSansPro, sans-serif'

// THEME
const theme = {
  // Color Palette
  palette: {
    common: {
      black,
      white
    },
    background: {
      paper: white,
      default: '#f6f7f8'
    },
    primary: {
      light: brandColor,
      main: brandColor,
      dark: brandSecondaryColor, // hover
      contrastText: white
    },
    // Dark background
    secondary: {
      light: white,
      main: white,
      dark: white, // hover
      contrastText: brandColor
    },
    error: {
      light: error,
      main: error,
      dark: error, // hover
      contrastText: white
    },
    confirm: {
      light: success,
      main: success,
      dark: success, // hover
      contrastText: white
    },
    text: {
      primary: black,
      secondary: 'rgba(0, 0, 0, 0.54)',
      disabled: 'rgba(0, 0, 0, 0.38)',
      hint: 'rgba(0, 0, 0, 0.38)'
    }
  },
  // Breakpoints
  breakpoints: {
    values: {
      xs: 0,
      sm: 480,
      md: 768,
      lg: 1024,
      xl: 1440
    }
  },
  // Overrides UI
  overrides: {
    // BUTTONS
    MuiButton: {
      // Name of the component ⚛️ / style sheet
      root: {
        borderRadius: '0.4rem',
        boxShadow: 'none',
        fontFamily,
        fontSize: '1.6rem',
        fontWeight: '600',
        height: '4rem',
        letterSpacing: '0.2rem',
        lineHeight: '1.7rem',
        margin: '0',
        minWidth: '18rem',
        padding: '0 2rem',
        pointer: 'cursor',
        width: 'auto',

        '&.Mui-disabled': {
          backgroundColor: brandColor,
          color: white,
          opacity: '0.3'
        },
        '&:hover': {
          boxShadow: 'none'
        },
        '&:active': {
          boxShadow: 'none'
        }
      },

      // SECONDARY BUTTON WITH WHITE BACKGROUND
      outlinedPrimary: {
        borderColor: brandColor,

        '&:hover': {
          backgroundColor: 'transparent',
          border: `.1rem solid ${brandSecondaryColor}`,
          color: brandSecondaryColor
        },
        '&:active': {
          boxShadow: 'none'
        },
        '&$disabled': {
          color: brandColor,
          opacity: '0.2'
        }
      },
      text: {
        color: brandColor,
        fontFamily,
        fontWeight: '600',
        letterSpacing: '0.2rem',
        lineHeight: '1.7rem',
        minWidth: 'unset',
        height: 'auto',
        padding: '0',
        textDecoration: 'underline',
        textTransform: 'unset',

        '&:hover': {
          backgroundColor: 'transparent',
          color: brandSecondaryColor,
          textDecoration: 'underline',
          textTransform: 'unset'
        },
        '&$disabled': {
          color: brandColor,
          opacity: '0.2',
          textDecoration: 'underline',
          textTransform: 'unset'
        }
      },
      textPrimary: {
        padding: '0',
        textDecoration: 'none',
        textTransform: 'unset',

        '&:hover': {
          backgroundColor: 'transparent',
          color: brandSecondaryColor,
          textDecoration: 'none',
          textTransform: 'unset'
        },
        '&$disabled': {
          color: brandColor,
          opacity: '0.2',
          textDecoration: 'none',
          textTransform: 'unset'
        }
      },
      textSecondary: {
        color: white,
        padding: '0',
        textDecoration: 'none',
        textTransform: 'unset',
        '&:hover': {
          backgroundColor: 'transparent',
          color: brandSecondaryColor,
          textDecoration: 'none',
          textTransform: 'unset'
        },
        '&$disabled': {
          color: brandColor,
          opacity: '0.2',
          textDecoration: 'none',
          textTransform: 'unset'
        }
      },
      startIcon: {
        alignItems: 'center',
        display: 'flex',
        height: '2rem',
        justifyContent: 'center',
        margin: '0 1rem 0 0',
        width: '2rem'
      }
    },
    // ALERT BADGE
    MuiAlert: {
      root: {
        alignItems: 'center',
        borderRadius: '0.8rem',
        boxShadow: '0 .4rem .8rem rgba(0, 0, 0, 0.2)',
        fontFamily,
        margin: '0 0 5rem',
        maxWidth: '144rem',
        minHeight: '5rem',
        padding: '1.5rem 2rem',
        width: '100%'
      },
      icon: {
        height: '3rem',
        margin: '0 2rem 0 0',
        padding: 0,
        width: '3rem'
      },
      message: {
        fontSize: '1.8rem',
        lineHeight: '1.5rem',
        fontWeight: '500',
        margin: 0,
        overflow: 'initial',
        padding: 0,
        width: '100%'
      },
      filledInfo: {
        backgroundColor: info
      },
      filledWarning: {
        backgroundColor: warning
      },
      filledError: {
        backgroundColor: error
      },
      filledSuccess: {
        backgroundColor: success
      }
    },
    // DIALOG DARK OVERLAY
    MuiBackdrop: {
      root: {
        backgroundColor: 'transparent'
      }
    },
    // DIALOG
    MuiDialog: {
      paper: {
        backgroundColor: white,
        borderRadius: '0.8rem',
        minHeight: '49rem',
        padding: '8rem 3rem 0'
      },
      paperWidthSm: {
        maxWidth: 'unset',
        width: '56rem'
      },
      paperScrollPaper: {
        maxHeight: 'unset',
        minHeight: '56rem'
      },
      paperFullScreen: {
        padding: '0'
      }
    },
    // PAPER
    MuiPaper: {
      root: {
        backgroundColor: white,
        position: 'relative'
      },
      rounded: {
        borderRadius: '0.2rem'
      },
      elevation24: {
        boxShadow: '.4rem .8rem 1.4rem 0 rgba(0, 0, 0, 0.5)'
      }
    },
    // DIALOG TITLE
    MuiDialogTitle: {
      root: {
        color: black,
        fontFamily,
        fontSize: '2.4rem !important',
        fontWeight: 500,
        margin: '0 0 3rem !important',
        padding: '0 !important',
        textAlign: 'center'
      }
    },
    // DIALOG CONTENT
    MuiDialogContent: {
      root: {
        fontFamily,
        fontSize: '1.8rem',
        padding: '0 !important',
        textAlign: 'center',
        overflow: 'unset'
      }
    },
    // DIALOG CONTENT TEXT
    MuiDialogContentText: {
      root: {
        fontFamily,
        textAlign: 'center'
      }
    },
    // DIALOG ACTION
    MuiDialogActions: {
      root: {
        justifyContent: 'center',
        margin: '4rem 0',
        padding: '0 !important'
      }
    },
    // HEADER FLOW
    MuiAppBar: {
      root: {
        alignItems: 'center',
        backgroundColor: brandColor,
        boxShadow: 'none',
        justifyContent: 'center',
        minHeight: '10rem',
        padding: '0 4rem',
        zIndex: 5
      },
      colorDefault: {
        boxShadow: 'none',
        backgroundColor: white,
        minHeight: '7.2rem',
        width: '100%',
        height: '10rem',
        padding: '0 6.5rem',
        justifyContent: 'center'
      }
    },
    // HEADER FLOW
    MuiToolbar: {
      regular: {
        height: '100%',
        justifyContent: 'space-between',
        margin: '0 auto',
        width: '100%'
      }
    },
    MuiListItemText: {
      root: {
        fontSize: '1.6rem',
        fontWeight: 500,
        padding: 0,
        margin: 0
      }
    },
    // FOMR CONTROL
    MuiFormControl: {
      root: {
        maxWidth: '31rem',
        minWidth: '100%',
        textAlign: 'left',
        width: '100%'
      }
    },
    // INPUT BASE
    MuiInputBase: {
      // INPUT Select
      root: {
        backgroundColor: white,
        border: 0,
        borderRadius: '0.2rem !important',
        boxShadow: '0 0.4rem 0.8rem 0 rgba(0, 0, 0, 0.2)',
        boxSizing: 'border-box',
        color: black,
        height: '5rem',
        fontFamily,
        fontSize: '1.8rem',
        margin: 0,
        maxWidth: '100%',
        outline: 'none',
        padding: 0,
        position: 'relative',
        width: '100%'
      },
      input: {
        alignItems: 'center',
        border: '.2rem solid transparent', // avoid wrong animation
        boxSizing: 'border-box',
        color: 'unset',
        display: 'flex',
        fontSize: '1.8rem',
        height: '5rem',
        zIndex: '1' // SUPER IMPORTANT, Don't Touch!
      },
      formControl: {
        padding: '0'
      },
      secondary: {
        root: {
          border: '.2rem solid lime'
        }
      }
    },
    // INPUT OUTLINED
    MuiOutlinedInput: {
      root: {
        '&.Mui-error': {
          border: `0.2rem solid ${error}` // box-input error
        }
      },
      input: {
        // testo interno all'input
        color: black,
        fontFamily,
        fontSize: '1.8rem',
        fontWeight: '500',
        lineHeight: '1.9rem'
      },
      notchedOutline: {
        border: 0 // toglie border al box
      }
    },
    // FORM LABEL
    MuiFormLabel: {
      root: {
        color: black, // avoid styleguide
        fontSize: '1.4rem !important',
        fontWeight: 'normal !important',
        zIndex: '20'
      },
      filled: {
        color: 'black !important',
        fontWeight: 'normal !important',
        transform: 'translate(0, -.3rem) !important'
      }
    },
    // INPUT LABEL
    MuiInputLabel: {
      // SELECT LABEL
      root: {
        color: 'rgba(0, 0, 0, 0.38) !important',
        fontFamily,
        fontSize: '1.8rem !important',
        fontWeight: '500 !important',
        letterSpacing: 'inherit',
        lineHeight: '1.7rem',
        paddingLeft: '1.5rem !important',
        pointerEvents: 'none',
        position: 'relative',
        top: '.2rem',
        zIndex: 1,

        '&:hover': {
          color: black,
          outline: 'none',
          padding: '0'
        },
        '&:focus': {
          outline: 'none',
          padding: '0'
        },
        '&.Mui-focused': {
          color: `${black} !important`,
          fontWeight: 'normal !important',
          transform: 'translate(0, -0.3rem)'
        }
      },
      formControl: {
        margin: '0',
        transform: 'translate(0, 3.2rem)',
        zIndex: '1'
      },
      shrink: {
        color: `${black} !important`,
        fontSize: '1.4rem !important',
        fontWeight: 'normal !important',
        padding: '0 !important',
        transform: 'translate(0, 3.2rem)'
      },
      animated: {
        transition: '0.3s',

        '&:focus': {
          transition: 'top 0.3s linear, left 0.3s linear, font-size 0.3s linear'
        }
      }
    },
    // SELECT
    MuiSelect: {
      input: {
        backgroundColor: white,
        maxWidth: '100%',
        width: '100%'
      },
      select: {
        backgroundColor: 'transparent',
        display: 'block',
        fontWeight: '500',
        height: 'auto',
        maxWidth: '100%',
        padding: '0 1.5rem !important',
        width: '100%',

        '&:error': {
          backgroundColor: 'transparent',
          outline: 'none'
        },
        '&:focus': {
          backgroundColor: 'transparent',
          outline: 'none'
        },
        '&:selected': {
          backgroundColor: 'transparent',
          outline: 'none'
        },
        '&.Mui-selected': {
          backgroundColor: white,
          '&:hover': {
            backgroundColor: 'transparent'
          }
        },
        '&.Mui-focusVisible': {
          backgroundColor: 'transparent'
        }
      },
      selectMenu: {
        alignItems: 'center',
        border: '0',
        display: 'block',
        height: '5rem',
        lineHeight: '5rem',
        textAlign: 'left',
        width: '23.3rem'
      },
      icon: {
        display: 'none'
      },
      iconOpen: {
        transform: 'rotate(180deg)'
      }
    },
    // FORM HELPER TEXT
    MuiFormHelperText: {
      root: {
        color: black,
        fontFamily,
        fontSize: '1.4rem',
        fontWeight: '600',
        letterSpacing: '0',
        lineHeight: '1.5rem',
        margin: '0',
        padding: '0',
        position: 'absolute',
        top: '7rem',

        '&.Mui-error': {
          color: error
        }
      }
    },
    // FORM CONTROL LABEL
    MuiFormControlLabel: {
      // Radio Button label
      root: {
        margin: '0',

        '&$disabled': {
          color: 'rgba(255, 255, 255, 0.5)'
        }
      }
    },
    // LIST
    MuiList: {
      // dropdown select
      root: {
        backgroundColor: white,
        margin: ' 0',
        maxWidth: '31rem',
        padding: '2rem 1.5rem !important',
        width: '100%',

        '&:focus': {
          backgroundColor: 'transparent'
        }
      }
    },
    // LIST ITEM
    MuiListItem: {
      // List item Dropdown
      root: {
        color: black,
        fontFamily: 'Nexi Sans !important',
        fontSize: '1.8rem !important',
        lineHeight: 'normal !important',
        minHeight: '3.5rem !important',
        margin: '10rem 1.5rem !important',
        padding: '1.5rem',
        whiteSpace: 'pre-line !important',

        '&$selected': {
          fontWeight: '600 !important',
          color: '#00b8de !important',
          fontFamily: 'Nexi Sans !important',
          backgroundColor: '#fff'
        },
        '&:hover': {
          backgroundColor: 'transparent !important', // avoid styleguide
          fontWeight: '500'
        }
      },
      button: {
        '&:focus': {
          backgroundColor: 'transparent !important'
        }
      }
    },
    // EXPANSION PANEL
    MuiExpansionPanel: {
      root: {
        height: '6rem'
      },
      '&.Mui-expanded': {
        height: '100%'
      }
    },
    // POPOVER
    MuiPopover: {
      paper: {
        boxShadow: '0 0.4rem 0.8rem 0 rgba(0, 0, 0, 0.2)',
        margin: '1.5rem 0 0 0',
        maxHeight: '30rem'
      }
    },
    // SWITCH
    MuiSwitch: {
      root: {
        height: '1.4rem',
        width: '5.5rem'
      },
      switchBase: {
        height: '2rem',
        width: '4rem',

        '&$disabled': {
          color: '#fff !important',

          '& + $bar': {
            backgroundColor: '#9596af !important',
            opacity: 0.5
          }
        }
      },
      '&.Mui-checked': {
        transform: 'translateX(2.1rem)'
      },
      bar: {
        backgroundColor: '#9596af',
        borderRadius: '2rem',
        boxShadow: '0 .2rem .6rem 0 rgba(0,0,0,0.2)',
        height: '2rem',
        width: '4rem'
      },
      colorPrimary: {
        color: '#fff',

        '&$checked': {
          color: '#fff',

          '& + $bar': {
            backgroundColor: '#2d32aa',
            opacity: 1
          }
        }
      }
    },
    // INPUT ADORNMENT
    MuiInputAdornment: {
      // label like +39
      root: {
        color: '#9a9b9c',
        height: '2.1rem',
        margin: '0',
        width: '4.2rem',
        zIndex: '1'
      },
      positionStart: {
        borderColor: '#9a9b9c',
        color: '#9a9b9c',
        height: '1.4rem',
        lineHeight: '1.4rem',
        fontSize: '1.8rem',
        borderWidth: '0 .1rem 0 0',
        borderStyle: 'solid',
        padding: '0.5rem 0 0',
        transition: 'opacity 0.3s linear',
        left: '0',
        width: '3rem',
        marginRight: '0'
      }
    },
    // MENU ITEM
    MuiMenuItem: {
      root: {
        backgroundColor: white,
        fontFamily,
        fontSize: '1.8rem',
        height: 'auto',
        fontWeight: 'normal !important',
        letterSpacing: 0,
        marginBottom: '1.5rem',
        padding: '0',
        whiteSpace: 'pre-wrap',

        '&:last-child': {
          marginBottom: '0'
        },
        '&:hover': {
          backgroundColor: 'transparent !important',
          fontWeight: '500 !important'
        },
        '&:focus': {
          backgroundColor: 'transparent !important'
        },
        '&.Mui-selected': {
          backgroundColor: 'transparent !important'
        }
      }
    },
    // LINEAR PROGRESS BAR
    MuiLinearProgress: {
      // LINEAR PROGRESSBAR (background bar)
      determinate: {
        width: '100%',
        borderRadius: '.4rem',
        height: '.6rem',
        maxWidth: '75rem'
      },
      buffer: {
        width: '100%',
        borderRadius: '.5rem',
        height: '1rem',
        maxWidth: '54.5rem',
        backgroundColor: 'transparent'
      },
      bar1Buffer: {
        backgroundColor: '#06EAFF',
        height: '1rem'
      },
      bar2Buffer: {
        backgroundColor: 'rgba(255,255,255,0.3)'
      },
      dashedColorPrimary: {
        display: 'none'
      },
      barColorPrimary: {
        // LINEAR PROGRESSBAR (value bar)
        backgroundColor: success,
        height: '.6rem'
      },
      barColorSecondary: {
        // LINEAR PROGRESSBAR (value bar)
        backgroundColor: white,
        height: '.6rem'
      },
      colorPrimary: {
        backgroundColor: 'rgba(72, 213, 151, 0.3)'
      },
      colorSecondary: {
        backgroundColor: 'rgba(255, 255, 255, 0.3)'
      }
    },
    // CHECKBOX BUTTON
    MuiCheckbox: {
      root: {
        border: '0',
        borderRadius: '0.3rem',
        backgroundColor: white,
        color: 'trasparent',
        height: '2rem',
        padding: '0',
        width: '2rem',

        '&.Mui-disabled': {
          color: brandColor,
          opacity: '0.3',

          '& svg': {
            height: '3rem',
            width: '3rem'
          }
        },

        '&:hover': {
          color: brandSecondaryColor,
          backgroundColor: white
        },

        '&$checked': {
          '&:hover': {
            backgroundColor: white
          },

          '&$disabled': {
            opacity: '0.3'
          }
        },

        '&$disabled': {
          opacity: '0.3',

          '&$checked': {
            color: brandColor
          }
        },

        '& svg': {
          height: '3rem',
          width: '3rem'
        },

        '& input': {
          height: '2rem',
          width: '2rem',

          '&$disabled': {
            cursor: 'disabled'
          }
        }
      }
    },
    // SLIDER
    MuiSlider: {
      root: {
        maxWidth: '23.6rem',
        width: '100%'
      },
      track: {
        backgroundColor: 'rgba(255, 255, 255, 0.6)',
        height: '.4rem',
        borderRadius: '.4rem'
      },
      thumb: {
        height: '1.5rem',
        width: '1.5rem',
        border: '.1rem solid #F0F0F0',
        backgroundColor: white,
        boxShadow: '0 .2rem .2rem 0 rgba(0, 0, 0, 0.3)',

        '&:hover': {
          boxShadow: 'none'
        },

        '&$disabled': {
          opacity: '0.6',
          height: '1.5rem',
          width: '1.5rem',
          border: '.1rem solid #F0F0F0',
          backgroundColor: white,
          boxShadow: '0 .2rem .2rem 0 rgba(0, 0, 0, 0.3)'
        }
      },
      trackBefore: {
        backgroundColor: '#00b8de',
        borderRadius: '.4rem',
        height: '.4rem',

        '&$disabled': {
          backgroundColor: 'rgba(0, 184, 222, 0.4)',
          borderRadius: '.4rem',
          height: '.4rem'
        }
      },
      trackAfter: {
        backgroundColor: 'rgba(255, 255, 255, 0.6)',
        borderRadius: '.4rem',
        height: '.4rem',

        '&$disabled': {
          backgroundColor: 'rgba(255, 255, 255, 0.4)',
          borderRadius: '.4rem',
          height: '.4rem'
        }
      }
    },
    // RADIO BUTTON
    MuiRadio: {
      // Radio Button
      root: {
        backgroundColor: 'transparent',
        border: '0',
        borderRadius: '.2rem',
        color: brandColor,
        height: '2.5rem',
        padding: '0',
        width: '2.5rem'
      }
    },
    // TOOLTIP
    MuiTooltip: {
      // style popper tooltip
      popper: {
        backgroundColor: white,
        boxShadow: '0 0 .8rem 0 rgba(0, 0, 0, 0.3)',
        borderRadius: '.3rem',
        fontSize: '1.4rem',
        left: '0',
        marginTop: '1rem',
        maxWidth: '30rem',
        opacity: '1',
        right: '0',
        padding: '0',
        width: 'auto'
      },
      // text inside popper
      tooltip: {
        backgroundColor: white,
        borderRadius: '0.8rem',
        color: black,
        fontFamily,
        fontSize: '1.6rem',
        lineHeight: 'normal',
        margin: '0 !important',
        maxWidth: '30rem',
        padding: '1.5rem'
      }
    },
    // ICON BUTTON
    MuiIconButton: {
      root: {
        padding: '0',

        '&:hover': {
          backgroundColor: 'transparent'
        }
      }
    },
    // TABLE
    MuiTableHead: {
      root: {
        // backgroundColor: black,
        backgroundColor: brandColor,
        minHeight: '4rem'
      }
    },
    MuiTableCell: {
      head: {
        color: white,
        fontFamily,
        fontWeight: '600',
        fontSize: '1.6rem',
        letterSpacing: '0.07rem',
        textAlign: 'left',
        textTransform: 'uppercase'
      },
      root: {
        fontFamily,
        fontWeight: 'normal',
        fontSize: '1.6rem',
        letterSpacing: '0',
        padding: '1rem 0 1rem 1.5rem',
        textAlign: 'left',
        width: 'min-content'
      }
    },
    MuiTableRow: {
      root: {
        minHeight: '4rem',

        '&.Mui-selected': {
          backgroundColor: 'rgba(45,50, 170, 0.2) !important'
        }
      }
    },
    // PAGINATION
    MuiTablePagination: {
      selectLabel: {
        fontFamily,
        fontSize: '1.8rem',
        fontWeight: 'normal',
        lineHeight: '1.9rem',
        letterSpacing: '0',
        textAlign: 'right'
      },
      displayedRows: {
        fontFamily,
        fontSize: '1.8rem',
        fontWeight: 'normal',
        lineHeight: '1.9rem',
        letterSpacing: '0'
      },
      menuItem: {
        fontFamily,
        fontSize: '1.8rem',
        fontWeight: 'normal',
        lineHeight: ' 1.9rem',
        letterSpacing: '0'
      },
      actions: {
        marginLeft: '3rem'
      }
    },
    MuiNativeSelect: {
      select: {
        '&:focus': {
          backgroundColor: 'transparent'
        }
      },
      icon: {
        fontSize: '2rem'
      }
    },
    // DATEPICKER
    MuiPickersCalendarHeader: {
      root: {
        alignItems: 'center',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        margin: '0',
        minHeight: '5rem',
        maxHeight: 'unset',
        padding: '0',
        position: 'relative'
      },
      label: {
        fontFamily,
        fontSize: '1.9rem',
        fontWeight: '500',
        lineHeight: '1.9rem',
        letterSpacing: '0',
        margin: '0'
      },
      labelContainer: {
        margin: '0',
        left: '50%',
        position: 'absolute',
        transform: 'translate(-50%,-50%)',
        top: '50%',
        zIndex: '1'
      },
      switchViewButton: {
        display: 'none'
      }
    },
    MuiPickersArrowSwitcher: {
      root: {
        maxWidth: '30rem',
        position: 'absolute',
        transform: 'translateY(-50%)',
        top: '50%',
        width: '100%'
      },
      spacer: {
        width: '100%'
      }
    },
    MuiDayPicker: {
      header: {
        borderBottom: '0.1rem solid rgba( 0, 0, 0, 0.3)',
        margin: '0 1.9rem .8rem'
      },
      weekDayLabel: {
        color: black,
        fontFamily,
        fontSize: '1.6rem !important',
        fontWeight: '500 !important',
        lineHeight: '1.7rem',
        letterSpacing: '0'
      }
    },
    MuiPickersDay: {
      root: {
        border: '0!important',
        color: black,
        fontFamily,
        fontSize: '1.6rem !important',
        fontWeight: '500 !important',
        lineHeight: '1.7rem',
        letterSpacing: '0',

        '&.Mui-selected': {
          backgroundColor: `${brandSecondaryColor} !important`
        },

        '&:hover': {
          backgroundColor: brandSecondaryColor,
          color: white
        }
      },
      today: {
        backgroundColor: `${white} !important`,
        border: `0.2rem solid ${brandColor} !important`,
        color: black,

        '&(.Mui-selected)': {
          backgroundColor: `${brandSecondaryColor} !important`,
          border: '0 !important'
        },
        '&:hover': {
          backgroundColor: `${brandSecondaryColor} !important`,
          color: white,
          border: '0 !important'
        }
      }
    },
    PrivatePickersYear: {
      button: {
        color: black,
        border: '0!important',
        fontFamily,
        fontSize: '1.6rem !important',
        fontWeight: '500 !important',
        lineHeight: '1.7rem',
        letterSpacing: '0',

        '&.Mui-selected': {
          backgroundColor: brandSecondaryColor,
          color: white
        },
        '&:hover': {
          backgroundColor: `${brandSecondaryColor} !important`,
          color: white
        }
      }
    },
    // TYPOGRAPHY
    MuiTypography: {
      root: {
        textTransform: 'initial'
      },
      title: {
        fontFamily
      },
      h1: {
        // Casi particolari (numeri) 48pt
        fontFamily,
        fontSize: '4.8rem',
        fontWeight: '500',
        lineHeight: '5.1rem',
        letterSpacing: '0.1rem'
      },
      h2: {
        // Casi particolari (numeri) 40pt
        fontFamily,
        fontSize: '4rem',
        fontWeight: '500',
        lineHeight: '4.3rem',
        letterSpacing: '0.1rem'
      },
      h3: {
        // Maxi titolo
        fontFamily,
        fontSize: '3.6rem',
        fontWeight: '500',
        lineHeight: '3.8rem',
        letterSpacing: '0.1rem'
      },
      h4: {
        // Titolo inizio/fine flusso (regular)
        fontFamily,
        fontSize: '2.8rem',
        lineHeight: '2.9rem',
        fontWeight: 'normal',
        letterSpacing: '0'
      },
      h5: {
        // Titolo Popup/Card
        fontFamily,
        fontSize: '2.4rem',
        lineHeight: '2.6rem',
        fontWeight: 'normal',
        letterSpacing: '0.1rem'
      },
      h6: {
        // Sottotitolo (azioni flusso)
        fontFamily,
        fontSize: '1.8rem',
        lineHeight: '2.2rem',
        fontWeight: 'normal',
        letterSpacing: '0.1rem'
      },
      subtitle1: {
        // Sottotitolo (azioni flusso)
        fontFamily,
        fontSize: '1.8rem',
        lineHeight: '2.2rem',
        fontWeight: 'normal',
        letterSpacing: '0.1rem'
      },
      subtitle2: {
        // Testo dettaglio
        fontFamily,
        letterSpacing: '0.1rem',
        fontSize: '1.6rem',
        lineHeight: '2.2rem',
        fontWeight: 'normal'
      },
      body1: {
        // Testo regular
        fontFamily,
        letterSpacing: '0.1rem',
        fontSize: '1.8rem',
        lineHeight: '1.9rem',
        fontWeight: 'normal'
      },
      body2: {
        // Didascalia 2° livello
        fontFamily,
        letterSpacing: '0.1rem',
        fontSize: '1.2rem',
        lineHeight: '1.2rem',
        fontWeight: 'normal'
      },
      caption: {
        // Didascalia/Testi tooltip
        fontFamily,
        color: black,
        fontSize: '1.4rem',
        letterSpacing: '0.1rem',
        lineHeight: '1.5rem'
      },
      overline: {
        // Label
        fontFamily,
        fontSize: '1.6rem',
        letterSpacing: '0.1rem',
        lineHeight: '1.7rem'
      },
      subheading: {
        color: black,
        fontFamily,
        fontSize: '1.8rem',
        lineHeight: '1.9rem'
      }
    },
    // TABS
    MuiTabs: {
      root: {
        border: 0,
        maxHeight: 'auto',
        height: 'auto',
        minHeight: '0'
      },
      indicator: {
        backgroundColor: brandSecondaryColor,
        height: '0.3rem'
      }
    },
    // TAB
    MuiTab: {
      root: {
        color: brandColor,
        fontFamily,
        fontSize: '1.8rem',
        fontWeight: 500,
        lineHeight: '1.9rem',
        height: 'auto',
        margin: '0 2rem 0 0',
        maxHeight: 'unset',
        minHeight: '0',
        minWidth: 'unset !important',
        padding: '0 0 .5rem 0',

        '&.Mui-selected': {
          color: brandSecondaryColor
        }
      }
    },
    // SKELETON
    MuiSkeleton: {
      root: {
        backgroundColor: 'rgba(190, 190, 190, .5)',

        '&::after': {
          background: 'linear-gradient(90deg, transparent, rgb(255 255 255 / 50%), transparent)'
        }
      }
    }
  },
  // Props for components
  props: {
    MuiButtonBase: {
      // Name of the component ⚛️
      disableRipple: true // ripple animation disabled
      // disableFocusRipple: true, // ripple animation disabled
    },
    MuiInput: {
      disableUnderline: true
    },
    MuiToolbar: {
      disableGutters: true
    },
    MuiDialog: {
      disableBackdropClick: true // prevent close on click out
    },
    MuiIconButton: {
      disableRipple: true // prevent hover animation
    }
  }
}

export default theme
